// Hideo - pages, their addresses and the menu structure.
// A page id is what app.switchTab() takes (switchTab('calendar')). Each page has an address handled by the router
// (src/router.ts): deep links and the browser's Back button work. The menus (SideNav, MobileMenu) and the section tabs at
// the top of the pages come from the tables below.
import { t, type Lang } from './i18n'

export type PageId = 'home' | 'ped' | 'calendar' | 'timeline' | 'vault' | 'keywords' | 'scripts' | 'published' | 'creators' | 'sources' | 'saved' | 'formats' | 'help' | 'settings'
type SectionId = 'home' | 'ped' | 'tools' | 'creators' | 'collections' | 'help' | 'settings'
/** [page, label, Font Awesome icon] */
type PageTab = readonly [PageId, string, string]

export const PAGE_PATHS: Record<PageId, string> = {
  home: '/', ped: '/plan', calendar: '/plan/calendar', timeline: '/plan/timeline',
  vault: '/tools/hooks', keywords: '/tools/trends', scripts: '/tools/scripts',
  published: '/plan/published', creators: '/creators', sources: '/collections/sources', saved: '/collections/saved', formats: '/collections/formats', help: '/help', settings: '/settings'
}
/** Old addresses of moved pages (bookmarks, links already shared): the router redirects them */
export const OLD_PATHS: Record<string, string> = { '/tools/keywords': '/tools/trends', '/published': '/plan/published', '/sources': '/collections/sources', '/saved': '/collections/saved' }
/** Page → menu section. Published videos are in the Plan; Hooks, Trends (page id keywords) and Scripts in "Tools"; Sources, Saved
 *  videos and Formats in "Collections" */
export const NAV_OF: Record<PageId, SectionId> = {
  home: 'home', ped: 'ped', calendar: 'ped', timeline: 'ped', vault: 'tools', keywords: 'tools', scripts: 'tools',
  published: 'ped', creators: 'creators', sources: 'collections', saved: 'collections', formats: 'collections', help: 'help', settings: 'settings'
}
const PED_TABS: readonly PageTab[] = [['ped', t('Board'), 'fa-table-columns'], ['calendar', t('Calendar'), 'fa-calendar-days'], ['timeline', t('Timeline'), 'fa-bars-progress'],
  ['published', t('Published'), 'fa-flag-checkered']]
const TOOL_TABS: readonly PageTab[] = [['vault', t('Hooks'), 'fa-anchor'], ['keywords', t('Trends'), 'fa-arrow-trend-up'], ['scripts', t('Scripts'), 'fa-pen-nib']]
const COLLECTION_TABS: readonly PageTab[] = [['sources', t('Sources'), 'fa-link'], ['saved', t('Saved videos'), 'fa-bookmark'], ['formats', t('Formats'), 'fa-clapperboard']]
/** Tabs at the top of the pages of each section */
export const SUB_TABS: Partial<Record<SectionId, readonly PageTab[]>> = { ped: PED_TABS, tools: TOOL_TABS, collections: COLLECTION_TABS }
/** Main menu: [section, label, icon, page it opens] (Plan, Tools and Collections also have their sub-pages, SUB_TABS) */
export const MAIN_SECTIONS: readonly (readonly [SectionId, string, string, PageId])[] = [
  ['home', t('Home'), 'fa-house', 'home'], ['ped', t('Plan'), 'fa-layer-group', 'ped'], ['tools', t('Tools'), 'fa-toolbox', 'vault'],
  ['creators', t('Creators'), 'fa-user-group', 'creators'], ['collections', t('Collections'), 'fa-box-archive', 'sources']
]

export const isPage = (x: unknown): x is PageId => typeof x === 'string' && Object.prototype.hasOwnProperty.call(NAV_OF, x)
export const pageOfPath = (path: string): PageId | null => {
  const raw = path.replace(/\/+$/, '') || '/', clean = OLD_PATHS[raw] || raw
  return (Object.keys(PAGE_PATHS) as PageId[]).find(p => PAGE_PATHS[p] === clean) || null
}

// Legal pages (texts in src/legal/<lang>/, drawn by src/pages/LegalPage.vue): not in the menus and never a start page.
// /privacy, /cookie, /copyright show the text in the UI language; /en/… and /it/… always in that language. The old
// static addresses (/privacy.html, /en/privacy.html…) redirect to the new ones (src/router.ts).
export const LEGAL_DOCS = ['privacy', 'cookie', 'copyright'] as const
export type LegalDoc = typeof LEGAL_DOCS[number]
/** The address of a legal page: in the UI language (lang null) or always in one language */
export const legalPath = (doc: LegalDoc, lang: Lang | null = null): string => (lang ? `/${lang}/` : '/') + doc
/** An address that shows a legal page, the old static ones included */
export const isLegalPath = (path: string): boolean => /^\/(?:(?:en|it)\/)?(?:privacy|cookie|copyright)(?:\.html)?\/?$/.test(path)

// The address the app was opened with: after the profile choice the app goes there instead of the start page (once)
let initialPage: PageId | null = location.pathname === '/' ? null : pageOfPath(location.pathname)
export function takeInitialPage(): PageId | null {
  const p = initialPage
  initialPage = null
  return p
}
